import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { calculateGrossPrice, resolveMarkup, type MarkupRule } from "@/lib/pricing";
import { resolveDynamicModifiers, applyDynamicModifier, type PricingRule, type ExperienceContext } from "@/lib/dynamic-pricing";
import type { Tables } from "@/integrations/supabase/types";
import { useLanguage } from "@/contexts/LanguageContext";

export type ExperienceWithPricing = Tables<"experiences"> & {
  grossPricePerPerson: number | null;
  grossPricePerGroup: number | null;
  markupPercent: number;
  season: string;
  in_season: boolean;
  dynamicModifierPercent: number;
};

async function fetchTranslations(language: string): Promise<Record<string, { title: string | null; short_description: string | null }>> {
  if (language === "en") return {};
  const { data } = await supabase
    .from("experience_translations")
    .select("experience_id, title, short_description")
    .eq("language_code", language);
  if (!data) return {};
  const map: Record<string, { title: string | null; short_description: string | null }> = {};
  for (const row of data) {
    map[row.experience_id] = { title: row.title, short_description: row.short_description };
  }
  return map;
}

async function fetchExperiencesWithPricing(language: string): Promise<ExperienceWithPricing[]> {
  const [expResult, markupResult, pricingResult, translations] = await Promise.all([
    supabase.from("experiences").select("*").eq("status", "active"),
    supabase.from("markup_rules").select("markup_percent, provider_id, experience_id"),
    supabase.from("pricing_rules").select("*").eq("is_active", true),
    fetchTranslations(language),
  ]);

  if (expResult.error) throw expResult.error;
  if (markupResult.error) throw markupResult.error;

  const rules = markupResult.data as MarkupRule[];
  const pricingRules = (pricingResult.data ?? []) as unknown as PricingRule[];

  const mapped = expResult.data.map((exp) => {
    const markup = resolveMarkup(rules, exp.id, exp.provider_id);
    const ctx: ExperienceContext = { id: exp.id, provider_id: exp.provider_id, season: exp.season };
    const { totalModifier } = resolveDynamicModifiers(
      pricingRules.filter((r) => r.rule_type === "seasonal"),
      ctx
    );

    const baseGrossPP = exp.net_price_per_person ? calculateGrossPrice(exp.net_price_per_person, markup) : null;
    const baseGrossPG = exp.net_price_per_group ? calculateGrossPrice(exp.net_price_per_group, markup) : null;

    const t = translations[exp.id];

    return {
      ...exp,
      title: t?.title || exp.title,
      short_description: t?.short_description || exp.short_description,
      markupPercent: markup,
      dynamicModifierPercent: totalModifier,
      grossPricePerPerson: baseGrossPP ? applyDynamicModifier(baseGrossPP, totalModifier) : null,
      grossPricePerGroup: baseGrossPG ? applyDynamicModifier(baseGrossPG, totalModifier) : null,
    };
  });
  return mapped.sort((a, b) => (b.in_season ? 1 : 0) - (a.in_season ? 1 : 0));
}

export function useExperiences() {
  const { language } = useLanguage();
  return useQuery({
    queryKey: ["experiences-public", language],
    queryFn: () => fetchExperiencesWithPricing(language),
  });
}

async function fetchFeaturedExperiences(language: string): Promise<ExperienceWithPricing[]> {
  const [expResult, markupResult, pricingResult, translations] = await Promise.all([
    supabase.from("experiences").select("*").eq("status", "active"),
    supabase.from("markup_rules").select("markup_percent, provider_id, experience_id"),
    supabase.from("pricing_rules").select("*").eq("is_active", true),
    fetchTranslations(language),
  ]);

  if (expResult.error) throw expResult.error;
  if (markupResult.error) throw markupResult.error;

  const rules = markupResult.data as MarkupRule[];
  const pricingRules = (pricingResult.data ?? []) as unknown as PricingRule[];

  const all = expResult.data.map((exp) => {
    const markup = resolveMarkup(rules, exp.id, exp.provider_id);
    const ctx: ExperienceContext = { id: exp.id, provider_id: exp.provider_id, season: exp.season };
    const { totalModifier } = resolveDynamicModifiers(
      pricingRules.filter((r) => r.rule_type === "seasonal"),
      ctx
    );

    const baseGrossPP = exp.net_price_per_person ? calculateGrossPrice(exp.net_price_per_person, markup) : null;
    const baseGrossPG = exp.net_price_per_group ? calculateGrossPrice(exp.net_price_per_group, markup) : null;

    const t = translations[exp.id];

    return {
      ...exp,
      title: t?.title || exp.title,
      short_description: t?.short_description || exp.short_description,
      markupPercent: markup,
      dynamicModifierPercent: totalModifier,
      grossPricePerPerson: baseGrossPP ? applyDynamicModifier(baseGrossPP, totalModifier) : null,
      grossPricePerGroup: baseGrossPG ? applyDynamicModifier(baseGrossPG, totalModifier) : null,
    };
  });

  const featured = all.filter((e) => (e as any).featured);
  if (featured.length > 0) return featured;
  return [...all].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 3);
}

export function useFeaturedExperiences() {
  const { language } = useLanguage();
  return useQuery({
    queryKey: ["experiences-featured", language],
    queryFn: () => fetchFeaturedExperiences(language),
  });
}
